import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import { NGROK } from "../../../../APIs";
import axios from "axios";
import { myLocalStorage } from "../../../../components/StorageHelper";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex flex-col w-full gap-4 font-sans">
    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
      <Sk className="h-9 w-16 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="text-white text-center py-3.5 font-semibold tracking-wide" style={{ backgroundColor: BLUE }}>
          Publish Roles
        </div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="flex-1 overflow-y-auto">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-9 w-9 rounded-full flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Sk className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1">
              <Sk className="h-5 w-2/3 rounded" />
              <Sk className="h-4 w-1/3 rounded mt-2" />
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-3">
            <Sk className="h-4 w-16 rounded" />
            <Sk className="h-4 w-24 rounded" />
          </div>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-5">
          <div>
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-2/3 rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-20 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
            <Sk className="h-4 w-5/6 rounded mt-2" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AzureGenericRoles = ({ subscription }) => {
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleActions, setSelectedRoleActions] = useState(null);
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [actionsTab, setActionsTab] = useState("actions");
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const [roles, setRoles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const getTheSelectedRoleDefinition = async (role) => {
    setIsDetailsLoading(true);
    try {
      const response = await axios.get(
        `${NGROK}/api/azureResources/v1/getAzureRoleDefinitionById?id=${role.id}&tenantName=${latestTenant}`
      );
      if (response.status === 200 && response?.data) {
        setSelectedRoleActions(response.data);
      } else {
        setSelectedRoleActions(null);
      }
    } catch (error) {
      setSelectedRoleActions(null);
    }
    setIsDetailsLoading(false);
  };

  const getAllRoles = async (subscription) => {
    const response = await axios.get(
      `${NGROK}/api/azureResources/v1/getRoleDefinitionsName?tenantName=${latestTenant}&subsId=${subscription?.azureSubscriptionId}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const fetchData = async (initialLoad = false) => {
    try {
      if (subscription === null) {
        setRoles([]);
        setSelectedRole(null);
        setLoading(false);
        return;
      };
      const roles = await getAllRoles(subscription);
      setRoles(roles);
      setLoading(false);
      if (initialLoad) {
        const filteredRoles = filterRoles(roles, filter);
        let selectRole = filteredRoles.length > 0 ? filteredRoles[0] : null;
        setSelectedRole(selectRole);
        if (selectRole) getTheSelectedRoleDefinition(selectRole);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      
    }
    setLoading(false);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    const filteredRoles = filterRoles(roles, newFilter);
    const selectRole = filteredRoles.length > 0 ? filteredRoles[0] : null;
    setSelectedRole(selectRole);
    setSelectedDetailsTab(0);
    if (selectRole) getTheSelectedRoleDefinition(selectRole);
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
    setActionsTab("actions");
    getTheSelectedRoleDefinition(role);
  };

  const filterRoles = (roles, filter) => {
    if (!roles) return [];
    if (filter === "default") {
      return roles.filter((role) => role.roleType === "BuiltInRole");
    } else if (filter === "custom") {
      return roles.filter((role) => role.roleType !== "BuiltInRole");
    }
    return roles;
  };

  const filteredRoles = filterRoles(
    roles?.filter(
      (role) =>
        role?.roleName &&
        role?.roleName.toLowerCase().includes(search.toLowerCase())
    ),
    filter
  );

  const handlePublishToggle = async (event, azureId, role) => {
    const published = event.target.checked;
    setRoles((prevData) =>
      prevData.map((r) =>
        r.azureId === azureId ? { ...r, published } : r
      )
    );

    let requestData = {
      resourceId: azureId,
      wsTenantName: latestTenant,
      flag: event.target.checked,
      type: "ROLE_DEFINITION",
      resourceAccountId: role?.subscriptionId,
    };

    try {
      await axios.patch(`${NGROK}/api/azureResources/v1/publish`, requestData);
    } catch (error) {
      // Revert on error
      setRoles((prevData) =>
        prevData.map((r) =>
          r.azureId === azureId ? { ...r, published: !published } : r
        )
      );
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData(true);
  }, [latestTenant, subscription]);

  const selectedKey = selectedRole?.id;
  const isBuiltIn = selectedRole?.roleType === "BuiltInRole";

  const renderActionsContent = () => {
    if (isDetailsLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    if (!selectedRoleActions) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">No actions available</p>
        </div>
      );
    }

    const actionsData = actionsTab === "actions"
      ? selectedRoleActions?.actions || []
      : selectedRoleActions?.notActions || [];

    return (
      <div>
        {/* Actions/NotActions Toggle */}
        <div className="flex gap-4 mb-4 border-b border-gray-200 dark:border-gray-700">
          {[
            { key: "actions", label: "Actions" },
            { key: "notActions", label: "Not Actions" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActionsTab(tab.key)}
              className={`pb-2 text-xs font-medium border-b-2 transition-all ${actionsTab === tab.key
                ? "border-b-2"
                : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              style={actionsTab === tab.key ? { color: BLUE, borderColor: BLUE } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {actionsTab === "actions" ? "Actions" : "Not Actions"}
          </span>
          <span
            className="px-2 py-0.5 text-xs font-semibold rounded-full"
            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
          >
            {actionsData?.length || 0}
          </span>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {actionsData?.length > 0 ? (
            actionsData.map((action, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: BLUE }} />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-mono break-all">{action}</span>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <VpnKeyIcon sx={{ fontSize: 32, color: "rgba(0,0,0,0.25)" }} />
              <p className="text-sm mt-2">No {actionsTab === "actions" ? "actions" : "not actions"} defined</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex flex-col w-full gap-4 font-sans">
          {/* Filter Tabs */}
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
            {[
              { key: "all", label: "All" },
              { key: "default", label: "Default" },
              { key: "custom", label: "Custom" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleFilterChange(tab.key)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${filter === tab.key
                  ? "text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                style={filter === tab.key ? { backgroundColor: BLUE } : undefined}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Roles List */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              <div
                className="text-white text-center py-3.5 font-semibold tracking-wide"
                style={{ backgroundColor: BLUE }}
              >
                Publish Roles
              </div>

              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ boxShadow: "none" }}
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.id || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-150 ${isSelected
                          ? "border-l-4"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent"
                          }`}
                        style={
                          isSelected
                            ? { backgroundColor: "rgba(59,131,246,0.06)", borderLeftColor: BLUE }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                          >
                            <SecurityIcon sx={{ color: BLUE, fontSize: 20 }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-gray-700 dark:text-white truncate">
                              {role.roleName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                              {role.roleType === "BuiltInRole" ? "Built-in Role" : "Custom Role"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data.azureId, data)}
                            data={role}
                            isChecked={role?.published}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                      >
                        <SecurityIcon sx={{ color: BLUE, fontSize: 22 }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-500 dark:text-white truncate">
                          {selectedRole?.roleName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {isBuiltIn ? "Built-in Role" : "Custom Role"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="px-5 pt-4">
                    <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setSelectedDetailsTab(0)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 0
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 0 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                        Info
                      </button>
                      <button
                        onClick={() => setSelectedDetailsTab(1)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 1
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 1 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <VpnKeyIcon fontSize="small" />
                        Permissions
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-5 flex-1 overflow-auto">
                    {selectedDetailsTab === 0 && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Name
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRole?.roleName || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role ID
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                            {selectedRole?.id || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Azure ID
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                            {selectedRole?.azureId || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Type
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRole?.roleType || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Description
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white leading-relaxed">
                            {selectedRoleActions?.description || selectedRole?.description || "—"}
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && renderActionsContent()}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AzureGenericRoles;
